import { useEffect, useRef } from "preact/hooks";
import { getApsDocument } from "../lib/apsDocumentRef";
import { extractLiveFromAccDocument } from "../lib/extractLiveFromViewer";
import { useAccModelStore } from "../stores/accModelStore";
import { useAccPipelineStore } from "../stores/accPipelineStore";
import { useEngineStore } from "../stores/engineStore";
import { usePropertiesStore } from "../stores/propertiesStore";
import { useViewerStore } from "../stores/viewerStore";

const LA_JURISDICTION_ID = "us-ca-la-county";

/**
 * After an APS-hosted model loads, extract Revit properties in-browser and
 * feed the rule engine (LA County / Casita-style mapping first).
 */
export function useLiveAccAnalysis(): void {
  const viewerUrn = useAccModelStore(s => s.viewerUrn);
  const enabledJurisdictions = useEngineStore(s => s.enabledJurisdictions);
  const inferred = useEngineStore(s => s.inferredJurisdictionIds);
  const viewer = useViewerStore(s => s.viewer);
  const ready = useViewerStore(s => s.ready);
  const setReady = useViewerStore(s => s.setReady);
  const extractionKey = useRef<string | null>(null);

  const laEnabled =
    !!enabledJurisdictions[LA_JURISDICTION_ID] ||
    inferred.includes(LA_JURISDICTION_ID);

  useEffect(() => {
    if (!viewerUrn) {
      extractionKey.current = null;
      return;
    }
    if (!viewer || !ready) {
      console.info("[Vera:ACC] Extraction waiting for viewer", {
        hasViewer: !!viewer,
        ready,
      });
      return;
    }
    if (!laEnabled) {
      console.info(
        "[Vera:ACC] Skipping live extraction — LA County rules are not enabled",
        { enabledJurisdictions, inferred }
      );
      useAccPipelineStore
        .getState()
        .complete(
          "Live property extraction skipped — enable Los Angeles County jurisdiction to analyze this model."
        );
      return;
    }

    const doc = getApsDocument();
    if (!doc) {
      console.warn("[Vera:ACC] APS document not available for extraction");
      return;
    }

    const key = `${viewerUrn}|la`;
    if (extractionKey.current === key) return;

    let cancelled = false;
    console.info(
      "[Vera:ACC] Starting live property extraction (full 3D model)"
    );
    useAccPipelineStore
      .getState()
      .setStep(
        "extracting",
        73,
        "Extracting Revit properties from full model…"
      );

    const restoreViewableId = useAccModelStore.getState().currentViewableId;
    const allViewables = useAccModelStore.getState().allViewables;
    const viewablesFor3d =
      allViewables.length > 0
        ? allViewables
        : useAccModelStore.getState().viewables;

    void extractLiveFromAccDocument(
      viewer,
      doc,
      viewablesFor3d,
      restoreViewableId,
      (percent, message) => {
        if (!cancelled) {
          useAccPipelineStore
            .getState()
            .setStep("extracting", percent, message);
        }
      }
    )
      .then(({ evaluatedElements, propertiesByDbId }) => {
        if (cancelled) return;
        extractionKey.current = key;
        useAccPipelineStore
          .getState()
          .setStep(
            "analyzing",
            96,
            `Evaluating ${evaluatedElements.length} elements against rules…`
          );
        useEngineStore.getState().setLiveElements(evaluatedElements);
        usePropertiesStore.getState().loadFromMap(propertiesByDbId);
        console.info(
          `[Vera:ACC] Live extraction complete: ${evaluatedElements.length} evaluable elements`
        );
        const emptyWarning =
          evaluatedElements.length === 0
            ? "No LA-evaluable Revit elements found in this model (doors, windows, rooms, stairs). Try the Sample ADU / Casita file or confirm Los Angeles County jurisdiction."
            : undefined;
        if (emptyWarning) {
          console.warn("[Vera:ACC]", emptyWarning);
        }
        setReady(true);
        useAccPipelineStore.getState().complete(emptyWarning);
      })
      .catch(err => {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : String(err);
        console.error("[Vera:ACC] Live property extraction failed", err);
        useAccPipelineStore.getState().setError(msg);
      });

    return () => {
      cancelled = true;
    };
  }, [viewerUrn, viewer, ready, laEnabled, setReady]);
}
